import { NavLink, useParams } from "react-router";
import { cn } from "@/lib/utils";

/**
 * The Brain's five faces.
 *
 * Tabs rather than five sidebar entries, because these are five ways of reading
 * **one** corpus rather than five areas of the app: the sources are what the
 * answers are built from, the history is what has been asked of them, and a
 * decision is an answer somebody committed to. Splitting them in the rail would
 * make the reader choose a place before they know which one holds what they
 * want, which is the failure the Brain exists to end.
 *
 * Routes, not local state. Each tab is a URL a reader can bookmark, send, and
 * come back to — and `/brain/ask/:questionId` is already a real address, so a
 * tab bar backed by `useState` would have had to fake one for that case anyway.
 *
 * **A `<nav>`, not an ARIA `tablist`** — it looks like tabs and it is not one.
 * `role="tab"` promises arrow-key navigation between tabs and a `tabpanel`
 * whose content swaps in place; these change the URL and remount a screen.
 * Claiming the role without honouring it leaves a screen-reader user pressing
 * arrow keys at something that does not answer. `aria-current="page"`, which
 * `NavLink` sets for us, is what actually describes this.
 */

type Tab = {
  to: string;
  label: string;
  /** Present when the tab is not built. Renders inert, and says why. */
  unavailable?: string;
};

const TABS: readonly Tab[] = [
  { to: "ask", label: "Ask" },
  // Beside Ask rather than under its box (the owner, 2026-09-27,
  // `product/0013`): the Ask tab is the question being asked and nothing else.
  // A question opens in Ask, so Ask is the tab lit while one is read.
  { to: "history", label: "History" },
  {
    to: "compile",
    label: "Compile",
    // Shown and disabled rather than omitted, at the product owner's call: the
    // bar keeps the shape the design has, so it does not shift under people
    // when this lands. It says "not yet" instead of pretending — an inert
    // control that explains itself beats a control that silently does nothing,
    // and beats a bar that changes width later.
    unavailable: "Compile isn’t built yet.",
  },
  { to: "decisions", label: "Decisions" },
  { to: "sources", label: "Sources" },
];

export function BrainTabs() {
  const { projectId } = useParams();

  return (
    <nav
      aria-label="Brain"
      className="mb-4 inline-flex items-center gap-1 rounded-card bg-subtle p-1"
    >
      {TABS.map((tab) =>
        tab.unavailable ? (
          <span
            key={tab.to}
            aria-disabled
            title={tab.unavailable}
            className={cn(
              "cursor-not-allowed rounded-input px-[18px] py-[7px]",
              "font-display text-[0.8rem] font-semibold tracking-[0.06em] uppercase",
              "text-muted/55",
            )}
          >
            {tab.label}
          </span>
        ) : (
          <NavLink
            key={tab.to}
            // Absolute, and built from the param rather than relative. These
            // render inside screens mounted at varying depths —
            // `/brain/ask/:questionId` is two segments deeper than
            // `/brain/sources` — and a relative `to` would resolve against
            // whichever one is showing, which is the trap the sidebar's own
            // docstring warns about.
            to={`/projects/${projectId}/brain/${tab.to}`}
            className={({ isActive }) =>
              cn(
                "rounded-input px-[18px] py-[7px] transition-colors",
                "font-display text-[0.8rem] font-semibold tracking-[0.06em] uppercase",
                isActive
                  ? "bg-surface text-ink shadow-tile"
                  : "text-muted hover:text-ink",
              )
            }
          >
            {tab.label}
          </NavLink>
        ),
      )}
    </nav>
  );
}
